import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { fetchCheckinDashboardMetrics } from '../services/api';
import { useAuth } from '../context/AuthContext';

const MAX_RANGE_DAYS = 90;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const CARD_RADIUS = 1.25;
const CHIP_RADIUS = 1;
const BAR_RADIUS = 0.9;

const EMPTY_SUMMARY = {
  totals: {
    ticketsDepartureToday: 0,
    eligibleForAutoWebcheckin: 0,
    boardingPassAvailable: 0
  },
  scheduled: { browserUse: 0, manual: 0, total: 0 },
  informationReadiness: { completeInfo: 0, missingInfo: 0 },
  completed: {
    browserUse: 0,
    manual: 0,
    total: 0,
    withinOneHourOfBrowserUseSchedule: 0
  },
  failed: { browserUse: 0, manual: 0, total: 0 },
  cancelled: { browserUse: 0, manual: 0, total: 0 },
  rates: {
    eligibilityRatePct: 0,
    scheduledRatePct: 0,
    completionRatePct: 0,
    boardingPassRatePct: 0,
    browserUseOneHourSlaRatePct: 0,
    failedRatePct: 0,
    cancelledRatePct: 0
  }
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toInt = (value) => Math.max(0, Math.round(toNumber(value)));

const formatCount = (value) => new Intl.NumberFormat('en-IN').format(toInt(value));

const formatRate = (value) =>
  `${new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2
  }).format(toNumber(value))}%`;

const getYmd = (dateObj) => {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultFilters = () => {
  const today = new Date();
  const endDate = new Date(today);
  endDate.setDate(today.getDate() - 1);
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - 7);

  return {
    startDate: getYmd(startDate),
    endDate: getYmd(endDate)
  };
};

const parseYmd = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const utcDate = new Date(Date.UTC(year, month - 1, day));

  if (
    utcDate.getUTCFullYear() !== year ||
    utcDate.getUTCMonth() !== month - 1 ||
    utcDate.getUTCDate() !== day
  ) {
    return null;
  }

  return utcDate;
};

const formatDateLabel = (value) => {
  const parsed = parseYmd(value);
  if (!parsed) return value || 'N/A';

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  }).format(parsed);
};

const CountChip = ({ label, value, textColor, borderColor }) => (
  <Chip
    variant="outlined"
    size="small"
    label={`${label}: ${formatCount(value)}`}
    sx={{
      height: 26,
      borderRadius: CHIP_RADIUS,
      borderColor: borderColor || '#CBD5E1',
      color: textColor || '#0F172A',
      backgroundColor: '#FFFFFF',
      '& .MuiChip-label': {
        fontSize: 11,
        fontWeight: 600,
        px: 1
      }
    }}
  />
);

const FunnelChart = ({ summary }) => {
  const steps = [
    { label: 'Tickets Departing', value: summary?.totals?.ticketsDepartureToday },
    { label: 'Eligible', value: summary?.totals?.eligibleForAutoWebcheckin },
    { label: 'Scheduled', value: summary?.scheduled?.total },
    { label: 'Completed', value: summary?.completed?.total },
    { label: 'Boarding Pass', value: summary?.totals?.boardingPassAvailable }
  ];

  const maxValue = Math.max(1, ...steps.map((step) => toInt(step.value)));
  const colors = ['#2563EB', '#0284C7', '#0891B2', '#0D9488', '#16A34A'];

  return (
    <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: CARD_RADIUS }}>
      <Typography variant="subtitle1" sx={{ mb: 1.25, fontWeight: 600 }}>
        Funnel
      </Typography>
      <Stack spacing={0.9}>
        {steps.map((step, index) => {
          const value = toInt(step.value);
          const width = `${Math.max((value / maxValue) * 100, value > 0 ? 8 : 0)}%`;

          return (
            <Stack key={step.label} direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ minWidth: 116, fontSize: 12, color: 'text.secondary' }}>
                {step.label}
              </Typography>
              <Box sx={{ flex: 1, backgroundColor: '#F1F5F9', borderRadius: BAR_RADIUS, overflow: 'hidden', height: 24 }}>
                <Box
                  sx={{
                    width,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    pr: 1,
                    backgroundColor: colors[index]
                  }}
                >
                  {value > 0 && (
                    <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600, fontSize: 11 }}>
                      {formatCount(value)}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Typography sx={{ minWidth: 28, textAlign: 'right', fontSize: 12, fontWeight: 600 }}>
                {formatCount(value)}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
};

const SourceSplitChart = ({ summary }) => {
  const rows = [
    { label: 'Scheduled', data: summary?.scheduled || { browserUse: 0, manual: 0, total: 0 } },
    { label: 'Completed', data: summary?.completed || { browserUse: 0, manual: 0, total: 0 } },
    { label: 'Failed', data: summary?.failed || { browserUse: 0, manual: 0, total: 0 } },
    { label: 'Cancelled', data: summary?.cancelled || { browserUse: 0, manual: 0, total: 0 } }
  ];

  return (
    <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: CARD_RADIUS }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Source Split</Typography>
        <Stack direction="row" spacing={0.5}>
          <Chip label="Browser-use" size="small" sx={{ backgroundColor: '#DBEAFE', color: '#1D4ED8', borderRadius: CHIP_RADIUS }} />
          <Chip label="Manual" size="small" sx={{ backgroundColor: '#FFEDD5', color: '#C2410C', borderRadius: CHIP_RADIUS }} />
        </Stack>
      </Stack>
      <Stack spacing={1.2}>
        {rows.map((row) => {
          const browserUse = toInt(row.data.browserUse);
          const manual = toInt(row.data.manual);
          const total = toInt(row.data.total || browserUse + manual);
          const browserWidth = total > 0 ? (browserUse / total) * 100 : 0;
          const manualWidth = total > 0 ? (manual / total) * 100 : 0;

          return (
            <Stack key={row.label} spacing={0.45}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{row.label}</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                  B {formatCount(browserUse)} / M {formatCount(manual)} / T {formatCount(total)}
                </Typography>
              </Stack>
              <Box sx={{ display: 'flex', borderRadius: BAR_RADIUS, overflow: 'hidden', height: 20, backgroundColor: '#E2E8F0' }}>
                <Box sx={{ width: `${browserWidth}%`, backgroundColor: '#2563EB' }} />
                <Box sx={{ width: `${manualWidth}%`, backgroundColor: '#F97316' }} />
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
};

const TrendChart = ({ breakdown }) => {
  const series = [
    { key: 'scheduled', label: 'Scheduled', color: '#2563EB' },
    { key: 'completed', label: 'Completed', color: '#16A34A' },
    { key: 'failed', label: 'Failed', color: '#DC2626' },
    { key: 'cancelled', label: 'Cancelled', color: '#D97706' }
  ];
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const width = 620;
  const height = 170;
  const margin = { top: 8, right: 10, bottom: 30, left: 30 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const yFractions = [0, 0.25, 0.5, 0.75, 1];

  const valuesBySeries = series.map((line) =>
    breakdown.map((item) => toInt(item?.metrics?.[line.key]?.total))
  );

  const allValues = valuesBySeries.flat();
  const maxValue = Math.max(1, ...allValues);
  const labelInterval = breakdown.length > 10 ? Math.ceil(breakdown.length / 6) : 1;
  const hoverBandWidth = breakdown.length > 1 ? plotWidth / (breakdown.length - 1) : plotWidth;

  const xPos = (index) => {
    if (breakdown.length <= 1) return margin.left + plotWidth / 2;
    return margin.left + (index / (breakdown.length - 1)) * plotWidth;
  };

  const yPos = (value) => margin.top + plotHeight - (toInt(value) / maxValue) * plotHeight;

  const buildPath = (values) =>
    values
      .map((value, index) => `${index === 0 ? 'M' : 'L'} ${xPos(index)} ${yPos(value)}`)
      .join(' ');

  const hoveredValues = hoveredIndex !== null
    ? series.map((line, lineIndex) => ({
        ...line,
        value: toInt(valuesBySeries[lineIndex][hoveredIndex])
      }))
    : [];
  const hoverX = hoveredIndex !== null ? xPos(hoveredIndex) : null;
  const tooltipWidth = 126;
  const tooltipHeight = 18 + hoveredValues.length * 12;
  const tooltipX = hoverX === null
    ? 0
    : Math.min(Math.max(hoverX + 8, margin.left), width - margin.right - tooltipWidth);
  const tooltipY = margin.top + 6;

  return (
    <Paper elevation={0} sx={{ p: 1.1, border: '1px solid', borderColor: 'divider', borderRadius: CARD_RADIUS }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 14 }}>Daily Trend</Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {series.map((line) => (
            <Chip
              key={line.key}
              label={line.label}
              size="small"
              sx={{
                backgroundColor: `${line.color}1A`,
                color: line.color,
                borderRadius: CHIP_RADIUS,
                height: 24,
                '& .MuiChip-label': { fontSize: 10, px: 1, py: 0.1 }
              }}
            />
          ))}
        </Stack>
      </Stack>

      {breakdown.length === 0 ? (
        <Alert severity="info">No date-wise trend available for the selected range.</Alert>
      ) : (
        <Box sx={{ width: '100%' }}>
          <svg
            viewBox={`0 0 ${width} ${height}`}
            width="100%"
            role="img"
            aria-label="Daily trend chart"
            onMouseLeave={() => setHoveredIndex(null)}
          >
            {yFractions.map((fraction) => {
              const y = margin.top + plotHeight * fraction;
              const yValue = Math.round(maxValue * (1 - fraction));
              return (
                <g key={fraction}>
                  <line
                    x1={margin.left}
                    y1={y}
                    x2={width - margin.right}
                    y2={y}
                    stroke="#E2E8F0"
                    strokeWidth="1"
                  />
                  <text
                    x={margin.left - 6}
                    y={y + 3}
                    fontSize="9"
                    fill="#94A3B8"
                    textAnchor="end"
                  >
                    {yValue}
                  </text>
                </g>
              );
            })}

            {breakdown.map((item, index) => {
              const rectX = Math.max(
                margin.left,
                Math.min(xPos(index) - hoverBandWidth / 2, width - margin.right - hoverBandWidth)
              );

              return (
                <rect
                  key={`hover-band-${item.date}`}
                  x={rectX}
                  y={margin.top}
                  width={hoverBandWidth}
                  height={plotHeight}
                  fill="transparent"
                  onMouseEnter={() => setHoveredIndex(index)}
                />
              );
            })}

            {hoveredIndex !== null && hoverX !== null && (
              <line
                x1={hoverX}
                y1={margin.top}
                x2={hoverX}
                y2={margin.top + plotHeight}
                stroke="#CBD5E1"
                strokeWidth="1"
                strokeDasharray="3 3"
              />
            )}

            {series.map((line, lineIndex) => (
              <g key={line.key}>
                <path
                  d={buildPath(valuesBySeries[lineIndex])}
                  fill="none"
                  stroke={line.color}
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {valuesBySeries[lineIndex].map((value, index) => (
                  <circle
                    key={`${line.key}-${index}`}
                    cx={xPos(index)}
                    cy={yPos(value)}
                    r={hoveredIndex === index ? 2.6 : 1.8}
                    fill={line.color}
                  />
                ))}
              </g>
            ))}

            {breakdown.map((item, index) => {
              const shouldRender = index === 0 || index === breakdown.length - 1 || index % labelInterval === 0;
              if (!shouldRender) return null;

              return (
                <text
                  key={`label-${item.date}`}
                  x={xPos(index)}
                  y={height - 8}
                  fontSize="9"
                  fill="#64748B"
                  textAnchor="middle"
                >
                  {formatDateLabel(item.date).slice(0, 6)}
                </text>
              );
            })}

            {hoveredIndex !== null && hoveredValues.length > 0 && (
              <g>
                <rect
                  x={tooltipX}
                  y={tooltipY}
                  width={tooltipWidth}
                  height={tooltipHeight}
                  rx="3"
                  fill="#FFFFFF"
                  stroke="#CBD5E1"
                />
                <text x={tooltipX + 7} y={tooltipY + 11} fontSize="9" fill="#334155" fontWeight="600">
                  {formatDateLabel(breakdown[hoveredIndex]?.date)}
                </text>
                {hoveredValues.map((item, i) => (
                  <text
                    key={`tip-${item.key}`}
                    x={tooltipX + 7}
                    y={tooltipY + 21 + i * 11}
                    fontSize="9"
                    fill={item.color}
                  >
                    {item.label}: {item.value}
                  </text>
                ))}
              </g>
            )}
          </svg>
        </Box>
      )}
    </Paper>
  );
};

const DailyBreakdownSection = ({ breakdown, isMobile }) => {
  if (breakdown.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: CARD_RADIUS }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Daily Breakdown
        </Typography>
        <Alert severity="info">No rows available for the selected filters.</Alert>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: CARD_RADIUS }}>
      <Typography variant="subtitle1" sx={{ mb: 1.25, fontWeight: 600 }}>
        Daily Breakdown
      </Typography>

      {isMobile ? (
        <Stack spacing={1}>
          {breakdown.map((day) => {
            const metrics = day?.metrics || EMPTY_SUMMARY;
            return (
              <Paper key={day.date} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: CARD_RADIUS, p: 1.1 }}>
                <Typography sx={{ fontWeight: 600, mb: 0.65, fontSize: 13 }}>{formatDateLabel(day.date)}</Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.6}>
                  <Chip label={`Tickets ${formatCount(metrics.totals?.ticketsDepartureToday)}`} size="small" sx={{ borderRadius: CHIP_RADIUS }} />
                  <Chip label={`Eligible ${formatCount(metrics.totals?.eligibleForAutoWebcheckin)}`} size="small" sx={{ borderRadius: CHIP_RADIUS }} />
                  <Chip label={`Scheduled ${formatCount(metrics.scheduled?.total)}`} size="small" sx={{ borderRadius: CHIP_RADIUS }} />
                  <Chip label={`Completed ${formatCount(metrics.completed?.total)}`} size="small" sx={{ borderRadius: CHIP_RADIUS }} />
                  <Chip label={`Failed ${formatCount(metrics.failed?.total)}`} size="small" sx={{ borderRadius: CHIP_RADIUS }} />
                  <Chip label={`Cancelled ${formatCount(metrics.cancelled?.total)}`} size="small" sx={{ borderRadius: CHIP_RADIUS }} />
                </Stack>
                <Stack direction="row" flexWrap="wrap" gap={0.6} sx={{ mt: 0.8 }}>
                  <Chip
                    label={`Completion ${formatRate(metrics.rates?.completionRatePct)}`}
                    size="small"
                    sx={{ backgroundColor: '#ECFDF5', color: '#166534', borderRadius: CHIP_RADIUS }}
                  />
                  <Chip
                    label={`Boarding Pass ${formatRate(metrics.rates?.boardingPassRatePct)}`}
                    size="small"
                    sx={{ backgroundColor: '#EFF6FF', color: '#1D4ED8', borderRadius: CHIP_RADIUS }}
                  />
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      ) : (
        <TableContainer>
          <Table size="small" aria-label="daily dashboard table">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell align="right">Tickets</TableCell>
                <TableCell align="right">Eligible</TableCell>
                <TableCell align="right">Scheduled</TableCell>
                <TableCell align="right">Completed</TableCell>
                <TableCell align="right">Boarding Pass</TableCell>
                <TableCell align="right">Failed</TableCell>
                <TableCell align="right">Cancelled</TableCell>
                <TableCell align="right">Completion %</TableCell>
                <TableCell align="right">Boarding Pass %</TableCell>
                <TableCell align="right">Browser SLA %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {breakdown.map((day) => {
                const metrics = day?.metrics || EMPTY_SUMMARY;
                return (
                  <TableRow key={day.date} hover>
                    <TableCell>{formatDateLabel(day.date)}</TableCell>
                    <TableCell align="right">{formatCount(metrics.totals?.ticketsDepartureToday)}</TableCell>
                    <TableCell align="right">{formatCount(metrics.totals?.eligibleForAutoWebcheckin)}</TableCell>
                    <TableCell align="right">{formatCount(metrics.scheduled?.total)}</TableCell>
                    <TableCell align="right">{formatCount(metrics.completed?.total)}</TableCell>
                    <TableCell align="right">{formatCount(metrics.totals?.boardingPassAvailable)}</TableCell>
                    <TableCell align="right">{formatCount(metrics.failed?.total)}</TableCell>
                    <TableCell align="right">{formatCount(metrics.cancelled?.total)}</TableCell>
                    <TableCell align="right">{formatRate(metrics.rates?.completionRatePct)}</TableCell>
                    <TableCell align="right">{formatRate(metrics.rates?.boardingPassRatePct)}</TableCell>
                    <TableCell align="right">{formatRate(metrics.rates?.browserUseOneHourSlaRatePct)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

const WebCheckinDashboardMetrics = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const initialFilters = useMemo(() => getDefaultFilters(), []);

  const [filters, setFilters] = useState(initialFilters);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [needsRelogin, setNeedsRelogin] = useState(false);

  const validateFilters = useCallback((nextFilters) => {
    const startDate = parseYmd(nextFilters.startDate);
    const endDate = parseYmd(nextFilters.endDate);

    if (!startDate || !endDate) {
      return 'Enter valid start and end dates in YYYY-MM-DD format.';
    }

    if (startDate > endDate) {
      return 'Start date must be earlier than or equal to end date.';
    }

    const inclusiveDays = Math.floor((endDate.getTime() - startDate.getTime()) / MS_PER_DAY) + 1;
    if (inclusiveDays > MAX_RANGE_DAYS) {
      return `Date range cannot exceed ${MAX_RANGE_DAYS} days (inclusive).`;
    }

    return '';
  }, []);

  const loadDashboardMetrics = useCallback(async (requestFilters) => {
    const validationError = validateFilters(requestFilters);
    if (validationError) {
      setError(validationError);
      setNeedsRelogin(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetchCheckinDashboardMetrics(requestFilters);
      setDashboardData(response);
      setNeedsRelogin(false);
    } catch (apiError) {
      if (apiError.status === 401) {
        setNeedsRelogin(true);
        setError('Session expired. Log in again to load dashboard metrics.');
      } else if (apiError.status === 400) {
        setError(apiError.message || 'Invalid date range. Please review filters and retry.');
      } else {
        setError(apiError.message || 'Failed to load dashboard metrics. Retry in a few seconds.');
      }
    } finally {
      setLoading(false);
    }
  }, [validateFilters]);

  useEffect(() => {
    loadDashboardMetrics(initialFilters);
  }, [initialFilters, loadDashboardMetrics]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApply = (event) => {
    event.preventDefault();
    loadDashboardMetrics(filters);
  };

  const handleRelogin = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const summary = dashboardData?.summary || EMPTY_SUMMARY;
  const breakdown = dashboardData?.dateWiseBreakdown || [];

  const kpis = [
    {
      label: 'Total Tickets',
      value: summary?.totals?.ticketsDepartureToday,
      borderColor: '#0F172A',
      textColor: '#0F172A'
    },
    {
      label: 'Eligible',
      value: summary?.totals?.eligibleForAutoWebcheckin,
      borderColor: '#BFDBFE',
      textColor: '#1D4ED8'
    },
    {
      label: 'Scheduled',
      value: summary?.scheduled?.total,
      borderColor: '#FDE68A',
      textColor: '#92400E'
    },
    {
      label: 'Completed',
      value: summary?.completed?.total,
      borderColor: '#BBF7D0',
      textColor: '#166534'
    },
    {
      label: 'Failed',
      value: summary?.failed?.total,
      borderColor: '#FECACA',
      textColor: '#991B1B'
    },
    {
      label: 'Cancelled',
      value: summary?.cancelled?.total,
      borderColor: '#FED7AA',
      textColor: '#9A3412'
    },
    {
      label: 'Boarding Pass',
      value: summary?.totals?.boardingPassAvailable,
      borderColor: '#A7F3D0',
      textColor: '#065F46'
    }
  ];

  const keyRates = [
    { label: 'Eligibility Rate', value: summary?.rates?.eligibilityRatePct },
    { label: 'Scheduled Rate', value: summary?.rates?.scheduledRatePct },
    { label: 'Completion Rate', value: summary?.rates?.completionRatePct },
    { label: 'Boarding Pass Rate', value: summary?.rates?.boardingPassRatePct },
    { label: 'Browser SLA (1h)', value: summary?.rates?.browserUseOneHourSlaRatePct },
    { label: 'Failed Rate', value: summary?.rates?.failedRatePct },
    { label: 'Cancelled Rate', value: summary?.rates?.cancelledRatePct }
  ];

  return (
    <Box sx={{ p: { xs: 1, md: 1.5 }, backgroundColor: '#F8FAFC', minHeight: '100%' }}>
      <Paper
        elevation={0}
        component="form"
        onSubmit={handleApply}
        sx={{
          p: 0.9,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: CARD_RADIUS,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          minHeight: 64,
          mb: 0.75
        }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ width: '100%' }}>
          <Typography variant="caption" sx={{ fontSize: 12, color: 'text.secondary', minWidth: 32 }}>
            Date
          </Typography>
          <TextField
            label="From"
            type="date"
            value={filters.startDate}
            onChange={(event) => handleFilterChange('startDate', event.target.value)}
            size="small"
            required
            InputLabelProps={{ shrink: true }}
            sx={{
              width: 152,
              '& .MuiInputBase-root': { height: 36, borderRadius: CHIP_RADIUS },
              '& .MuiInputLabel-root': { fontSize: 11 },
              '& .MuiInputBase-input': { fontSize: 13, py: 0.45 }
            }}
          />
          <TextField
            label="To"
            type="date"
            value={filters.endDate}
            onChange={(event) => handleFilterChange('endDate', event.target.value)}
            size="small"
            required
            InputLabelProps={{ shrink: true }}
            sx={{
              width: 152,
              '& .MuiInputBase-root': { height: 36, borderRadius: CHIP_RADIUS },
              '& .MuiInputLabel-root': { fontSize: 11 },
              '& .MuiInputBase-input': { fontSize: 13, py: 0.45 }
            }}
          />
          <Button type="submit" size="small" variant="contained" disabled={loading} sx={{ minHeight: 36, minWidth: 46, px: 1, fontSize: 12, borderRadius: CHIP_RADIUS }}>
            Go
          </Button>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: 1.35, border: '1px solid', borderColor: 'divider', borderRadius: CARD_RADIUS, mb: 1.1 }}>
        <Typography variant="subtitle1" sx={{ mb: 0.9, fontWeight: 600, fontSize: 16 }}>
          Key Data & Rates
        </Typography>
        <Stack spacing={0.85}>
          <Stack direction="row" spacing={0.35} flexWrap="wrap" justifyContent="flex-start" useFlexGap>
            {kpis.map((kpi) => (
              <CountChip
                key={kpi.label}
                label={kpi.label}
                value={kpi.value}
                borderColor={kpi.borderColor}
                textColor={kpi.textColor}
              />
            ))}
          </Stack>
          <Box
            sx={{
              borderTop: '1px dashed',
              borderColor: 'divider',
              my: 0.2
            }}
          />
          <Stack direction="row" gap={0.65} flexWrap="wrap" useFlexGap>
            {keyRates.map((rate) => (
              <Chip
                key={rate.label}
                label={`${rate.label}: ${formatRate(rate.value)}`}
                variant="outlined"
                size="small"
                sx={{ borderRadius: CHIP_RADIUS, '& .MuiChip-label': { fontSize: 11, px: 1.2 } }}
              />
            ))}
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert
          severity={needsRelogin ? 'warning' : 'error'}
          sx={{ mb: 1.25 }}
          action={
            needsRelogin ? (
              <Button color="inherit" size="small" onClick={handleRelogin}>
                Re-login
              </Button>
            ) : (
              <Button color="inherit" size="small" onClick={() => loadDashboardMetrics(filters)}>
                Retry
              </Button>
            )
          }
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: CARD_RADIUS,
            p: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1
          }}
        >
          <CircularProgress size={18} />
          <Typography sx={{ fontSize: 13 }}>Loading dashboard metrics...</Typography>
        </Paper>
      ) : (
        <Stack spacing={1.25}>
          <TrendChart breakdown={breakdown} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1.1fr 1fr' },
              gap: 1.25
            }}
          >
            <FunnelChart summary={summary} />
            <SourceSplitChart summary={summary} />
          </Box>

          <DailyBreakdownSection breakdown={breakdown} isMobile={isMobile} />
        </Stack>
      )}
    </Box>
  );
};

export default WebCheckinDashboardMetrics;
